import NextImage from 'next/image';
import styled, { css } from 'styled-components';
import transparentize from 'polished/lib/color/transparentize';

import type { PrefixProperties } from '@tmh-website/shared/types';
import { Color } from '@tmh-website/shared/colors';

export type FooterProps = PrefixProperties<
  {
    /**
     * Optional override for the default background color.
     *
     * @default Color.Blue_1000_Deep
     */
    backgroundColor?: Color;
  },
  '$'
>;

export const Footer = styled.footer<FooterProps>`
  padding: ${({ theme }) => theme.gap.large} 0;
  color: ${({ theme }) => theme.color[Color.Neutral_0_White]};
  background: ${({ theme, $backgroundColor }) => theme.color[$backgroundColor || Color.Blue_1000_Deep]};
`;

export const TopRow = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: stretch;
  justify-content: space-between;
  margin: -20px -16px;
  @media (${({ theme }) => theme.breakpoint.sm.up}) {
    margin: -30px -20px;
  }
  overflow: hidden;
`;

export const SiteLinksCol = styled.div`
  flex: 1 0 147px;
  margin: 20px 16px;
  @media (${({ theme }) => theme.breakpoint.sm.up}) {
    margin: 30px 20px;
  }

  > * {
    margin-bottom: 40px;
    @media (${({ theme }) => theme.breakpoint.sm.up}) {
      margin-bottom: 30px;
    }
    @media (${({ theme }) => theme.breakpoint.lg.up}) {
      margin-bottom: 30px;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

export const Divider = styled.hr`
  margin: 40px 0;
  border: none;
  border-bottom: 1px solid ${({ theme }) => transparentize(0.8, theme.color[Color.Neutral_0_White])};
  @media (${({ theme }) => theme.breakpoint.md.up}) {
    margin: 58px 0 64px;
  }
`;

export const BottomRow = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: stretch;
  justify-content: center;
  margin: -20px -16px;
  overflow: hidden;
  text-align: center;
  @media (${({ theme }) => theme.breakpoint.sm.up}) {
    justify-content: space-between;
    text-align: left;
  }
  @media (${({ theme }) => theme.breakpoint.lg.up}) {
    margin: -20px;
  }
`;

interface BottomColProps {
  $width?: number;
  $colMargin?: number;
  $desktopAlign?: 'left' | 'center' | 'right';
}

export const BottomCol = styled.div<BottomColProps>`
  flex: 0 0 auto;
  max-width: 300px;
  margin: 20px 16px;
  ${({ $width }) =>
    $width &&
    css`
      width: ${$width}px;
      flex: 0 0 ${$width}px;
    `}
  @media (${({ theme }) => theme.breakpoint.lg.up}) {
    margin: 20px;
  }
  @media (${({ theme }) => theme.breakpoint.xl.up}) {
    ${({ $desktopAlign }) => $desktopAlign && `text-align: ${$desktopAlign};`}
  }

  > * {
    margin-bottom: ${({ $colMargin }) => $colMargin ?? 40}px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

export const TopLogoWrapper = styled.div`
  text-align: center;
  @media (${({ theme }) => theme.breakpoint.md.up}) {
    text-align: right;
  }
`;

export const TopLogo = styled(NextImage)`
  width: 294.7px;
  max-width: 100%;
  height: auto;
`;
